// Copyright © 2026 Alex Bruce. All rights reserved.
//
// Phases, backed by the organisation.
//
// They decide how the Schedule and Cash Flow tabs group and colour work, and
// the order Auto-Distribute lays it out in -- so two estimators with different
// phase definitions produce different schedules from the same estimate. Kept
// in each browser, neither had any way to know they disagreed.
//
// Same diff-not-replace discipline as orgLessons: a write touches only what
// changed, so two people editing different phases cannot erase each other.

import { supabase } from "./supabase";
import type { PhaseGroup } from "./phaseGroups";

interface Row {
  slug: string;
  name: string;
  color: string;
  div_ids: string[];
  sort_order: number;
}

const rowToGroup = (r: Row): PhaseGroup => ({
  id: r.slug,
  name: r.name,
  color: r.color,
  divIds: r.div_ids ?? [],
  order: r.sort_order ?? 0,
});

const groupToRow = (g: PhaseGroup, orgId: string) => ({
  org_id: orgId,
  slug: g.id,
  name: g.name,
  color: g.color,
  div_ids: g.divIds ?? [],
  sort_order: g.order ?? 0,
});

export async function loadOrgPhaseGroups(orgId: string): Promise<PhaseGroup[]> {
  if (!supabase) return [];
  const { data, error } = await supabase
    .from("org_phase_groups")
    .select("slug,name,color,div_ids,sort_order")
    .eq("org_id", orgId)
    .order("sort_order", { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []).map(rowToGroup);
}

/**
 * Give a new org the default phase set, so its first schedule groups sensibly
 * instead of dropping every division into one bucket.
 *
 * ignoreDuplicates against the unique (org_id, slug) index makes this safe if
 * two people open Settings on a new org at once.
 */
export async function seedOrgPhaseGroups(
  orgId: string, defaults: PhaseGroup[],
): Promise<PhaseGroup[]> {
  if (!supabase || defaults.length === 0) return [];
  const { error } = await supabase
    .from("org_phase_groups")
    .upsert(defaults.map(g => groupToRow(g, orgId)),
            { onConflict: "org_id,slug", ignoreDuplicates: true });
  if (error) throw new Error(error.message);
  return loadOrgPhaseGroups(orgId);
}

const same = (a: PhaseGroup, b: PhaseGroup) =>
  a.name === b.name && a.color === b.color && a.order === b.order &&
  JSON.stringify(a.divIds ?? []) === JSON.stringify(b.divIds ?? []);

export interface SyncResult { items: PhaseGroup[]; errors: string[] }

export async function syncOrgPhaseGroups(
  orgId: string, prev: PhaseGroup[], next: PhaseGroup[],
): Promise<SyncResult> {
  if (!supabase) return { items: next, errors: [] };
  const prevById = new Map(prev.map(p => [p.id, p]));
  const nextById = new Map(next.map(n => [n.id, n]));
  const errors: string[] = [];

  const removed = prev.filter(p => !nextById.has(p.id));
  const added   = next.filter(n => !prevById.has(n.id));
  const changed = next.filter(n => {
    const before = prevById.get(n.id);
    return before && !same(before, n);
  });

  if (removed.length) {
    const { error } = await supabase.from("org_phase_groups").delete()
      .eq("org_id", orgId).in("slug", removed.map(r => r.id));
    if (error) errors.push(`Could not delete a phase: ${error.message}`);
  }
  // Changes and additions are the same write -- slug is the key, so an upsert
  // covers a rename, a recolour, a re-order and a brand-new phase alike.
  const upserts = [...changed, ...added];
  if (upserts.length) {
    const { error } = await supabase.from("org_phase_groups")
      .upsert(upserts.map(g => groupToRow(g, orgId)), { onConflict: "org_id,slug" });
    if (error) errors.push(`Could not save phases: ${error.message}`);
  }

  try {
    return { items: await loadOrgPhaseGroups(orgId), errors };
  } catch (e: any) {
    return { items: next, errors: [...errors, e.message] };
  }
}
